import { LanguagesEnum } from '@/api/enums';
import { equal, ilike } from '@/api/helpers';
import { client } from '@/client';
import {
  CampaignTemplate,
  SimulationPlanData,
  SimulationSchedulerItem,
} from '@/client/campaigns';
import { CampaignCatalogueItem } from '@/client/campaigns/types/catalogue.type';
import { SystemRoles } from '@/client/users';
import { LoadingStatuses } from '@/common/constants';
import { CatalogueSelectorModal } from '@/components/campaigns';
import {
  RECOMMENDED_CAMPAIGN_NAME,
  RECOMMENDED_SIMULATION_ID,
} from '@/components/campaigns/recommendation/constants';
import { PlanSimulationModal } from '@/components/campaigns/simulations/modals/PlanSimulationModal';
import {
  useCampaignTemplate,
  useCampaignTemplates,
} from '@/hooks/query/campaign-templates.hook';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGenerateCustomizedSimulation } from '@/hooks/useGenerateCustomizedSimulation';

import AiTargetedPhishingPlaceholder from '@/images/placeholders/ai-targeted-phishing-card.svg';
import BrowseCampaignsPlaceholder from '@/images/placeholders/browse-catalogue.svg';
import PageTemplatePlaceholder from '@/images/placeholders/page-template.svg';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { AppButton } from '@/ui';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)`
  height: auto;
  width: 100%;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--big-padding);

  p {
    text-align: center;
    line-height: var(--medium-line-height);
    font-weight: 500;
  }

  img {
    height: auto;
    width: 100%;
    margin-bottom: var(--default-padding);
  }

  .p-button {
    padding-inline: var(--big-padding);
    font-size: var(--large-font-size);
  }
`;

const StyledHeading = styled.h3`
  font-size: var(--heading-medium-font-size);
  line-height: var(--heading-medium-line-height);
  font-weight: 700;
  margin-bottom: var(--big-padding);
`;

const StyledCard = styled(Card)`
  min-width: 280px;
  max-width: 369px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: start;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background-color: var(--white-main);
  transition: box-shadow 0.2s ease;
  border-radius: var(--semi-medium-border-radius);

  .p-card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    padding: var(--default-padding);
  }

  .p-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0;
  }
`;

const StyledAppChip = styled(AppChip)`
  width: fit-content;
  font-size: var(--xsmall-font-size);
  height: 20px;
  margin-bottom: var(--xxsmall-padding);
`;

const StyledCampaignName = styled.h3`
  font-size: var(--heading-small-font-size);
  margin: 0;
  font-weight: 600;
  line-height: 150%;
  color: var(--black-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledDescription = styled.div`
  font-size: var(--small-font-size);
  line-height: 1.4;
  margin: var(--xsmall-padding) 0 var(--default-padding) 0;
  color: var(--black-main);
`;

const StyledThumbnailContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--box-border-radius);
  margin: 0 0 var(--medium-padding) 0;
  overflow: hidden;
  position: relative;
  background-color: var(--gray-pale);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const StyledButton = styled(AppButton)`
  width: fit-content;
  padding: var(--semi-medium-padding) var(--default-padding) !important;
  font-size: var(--small-font-size) !important;
  margin-top: auto;
`;

export const EmptyAutoPhishingPage: React.FC<{
  selectedTask: SimulationSchedulerItem | null;
  setSelectedTask: (task: SimulationSchedulerItem | null) => void;
  handlePlanSimulation: (data: SimulationPlanData) => Promise<void>;
}> = ({ selectedTask, setSelectedTask, handlePlanSimulation }) => {
  const { t } = useTranslation();
  const { accountCanUseFeature } = useFeatureFlag();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const isFreeTrial =
    !!currentAccount?.freeTrialEndsAt &&
    [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
      currentUser?.role?.code as SystemRoles,
    );
  const {
    isPreparing,
    isFailed,
    handleLaunch: handleLaunchAiTargetedPhishing,
    modals: aiTargetedPhishingModals,
  } = useGenerateCustomizedSimulation({
    isActive: isFreeTrial,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRecommendedSimulation, setIsRecommendedSimulation] = useState(false);
  const [isCatalogueOpen, setIsCatalogueOpen] = useState(false);
  const [selectedTemplateIdFromCatalogue, setSelectedTemplateIdFromCatalogue] =
    useState<string | undefined>(undefined);
  const [catalogueResetKey, setCatalogueResetKey] = useState(0);
  const [recommendedMasterItem, setRecommendedMasterItem] = useState<
    CampaignCatalogueItem | undefined
  >(undefined);
  const [isResolvingRecommended, setIsResolvingRecommended] = useState(false);
  const {
    isLoading: isLoadingRecommendedSimulations,
    campaignTemplates: recommendedSimulations,
  } = useCampaignTemplates({
    take: 1,
    skip: 0,
    filters: [
      equal('isPublished', true),
      ilike('name', RECOMMENDED_CAMPAIGN_NAME),
      equal('language', currentUser?.language || LanguagesEnum.EN),
    ],
  });

  const { campaignTemplate, isLoading } = useCampaignTemplate({
    campaignId:
      selectedTemplateIdFromCatalogue ??
      recommendedSimulations?.result?.[0]?.id ??
      RECOMMENDED_SIMULATION_ID,
  });

  const isTemplateLevelView = accountCanUseFeature(
    PostHogFeatureFlags.PHISHING_TEMPLATE_LEVEL_VIEW,
    currentAccount?.featureFlagAbility ?? [],
  );

  const fetchRecommendedMasterItem = async (): Promise<
    CampaignCatalogueItem | undefined
  > => {
    if (!currentAccount?.id || !campaignTemplate?.id) {
      return undefined;
    }

    try {
      const response = await client.campaignTemplates.getCatalogueTemplates(
        currentAccount.id,
        { filters: [equal('id', campaignTemplate.id)], take: 1 },
      );
      const item = response?.items?.[0];

      return response?.view === 'master' && item?.masterTemplateId
        ? item
        : undefined;
    } catch {
      return undefined;
    }
  };

  const handleScheduleRecommended = async () => {
    if (isTemplateLevelView) {
      setIsResolvingRecommended(true);
      setRecommendedMasterItem(await fetchRecommendedMasterItem());
      setIsResolvingRecommended(false);
    }

    setIsRecommendedSimulation(true);
    setIsModalOpen(true);
  };

  // Stable between renders: the modal resets its form whenever this changes,
  // so a fresh object would undo the admin's language or template choice.
  const recommendedCampaignTemplate = useMemo<
    (CampaignTemplate & Partial<CampaignCatalogueItem>) | undefined
  >(
    () =>
      recommendedMasterItem &&
      campaignTemplate &&
      !selectedTemplateIdFromCatalogue
        ? {
            ...campaignTemplate,
            id: recommendedMasterItem.id,
            name: recommendedMasterItem.name,
            masterTemplateId: recommendedMasterItem.masterTemplateId,
            variants: recommendedMasterItem.variants,
          }
        : undefined,
    [recommendedMasterItem, campaignTemplate, selectedTemplateIdFromCatalogue],
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRecommendedMasterItem(undefined);
    setSelectedTask(null);
    setIsRecommendedSimulation(false);
    setSelectedTemplateIdFromCatalogue(undefined);
    setCatalogueResetKey((k) => k + 1);
  };

  return (
    <>
      <StyledContainer direction="column" flex="1">
        <StyledHeading>{t('phishing.simulations.noCampaigns')}</StyledHeading>
        <FlexContainer gap={22} align="stretch">
          {!isFreeTrial && (
            <StyledCard>
              <StyledThumbnailContainer>
                {isLoadingRecommendedSimulations ||
                isLoading ||
                !campaignTemplate ? (
                  <Skeleton height="100%" width="100%" />
                ) : (
                  <img
                    src={
                      campaignTemplate?.thumbnail?.signedUrl ??
                      PageTemplatePlaceholder
                    }
                    alt={campaignTemplate?.name}
                  />
                )}
              </StyledThumbnailContainer>
              <StyledAppChip
                label={t('phishing.simulations.emptyPage.recommendedChip')}
                type="green"
              />
              <StyledCampaignName>
                {t('phishing.simulations.emptyPage.recommendedTitle')}
              </StyledCampaignName>
              <StyledDescription
                dangerouslySetInnerHTML={{
                  __html: t(
                    'phishing.simulations.emptyPage.recommendedDescription',
                  ),
                }}
              />
              <StyledButton
                label={t('phishing.simulations.emptyPage.scheduleSimulation')}
                onClick={handleScheduleRecommended}
                severity="success"
                state={
                  isLoadingRecommendedSimulations ||
                  isLoading ||
                  isResolvingRecommended
                    ? LoadingStatuses.LOADING
                    : LoadingStatuses.IDLE
                }
              />
            </StyledCard>
          )}

          <StyledCard>
            <StyledThumbnailContainer>
              <img src={BrowseCampaignsPlaceholder} alt="" />
            </StyledThumbnailContainer>
            <StyledCampaignName>
              {t('phishing.simulations.emptyPage.browseTitle')}
            </StyledCampaignName>
            <StyledDescription
              dangerouslySetInnerHTML={{
                __html: t('phishing.simulations.emptyPage.browseDescription'),
              }}
            />
            <StyledButton
              label={t('phishing.simulations.emptyPage.browseCatalogue')}
              onClick={() => setIsCatalogueOpen(true)}
              type="outlined"
            />
          </StyledCard>

          {isFreeTrial && (
            <StyledCard>
              <StyledThumbnailContainer>
                <img src={AiTargetedPhishingPlaceholder} alt="" />
              </StyledThumbnailContainer>
              <StyledAppChip
                label={t(
                  'phishing.simulations.emptyPage.aiTargetedPhishing.pill',
                )}
                type="secondary"
              />
              <StyledCampaignName>
                {t('phishing.simulations.emptyPage.aiTargetedPhishing.title')}
              </StyledCampaignName>
              <StyledDescription
                dangerouslySetInnerHTML={{
                  __html: t(
                    'phishing.simulations.emptyPage.aiTargetedPhishing.description',
                  ),
                }}
              />
              <StyledButton
                label={
                  isPreparing
                    ? t('phishing.simulations.modal.reconai.preparing')
                    : isFailed
                    ? t('phishing.simulations.modal.reconai.failed')
                    : t(
                        'phishing.simulations.emptyPage.aiTargetedPhishing.button',
                      )
                }
                onClick={handleLaunchAiTargetedPhishing}
                isDisabled={isPreparing}
                type="outlined"
              />
            </StyledCard>
          )}
        </FlexContainer>
      </StyledContainer>

      <PlanSimulationModal
        selectedTask={selectedTask}
        isRecommendedSimulation={isRecommendedSimulation}
        selectedCampaignTemplate={
          recommendedCampaignTemplate ?? campaignTemplate
        }
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onPlanSimulation={handlePlanSimulation}
      />
      <CatalogueSelectorModal
        key={catalogueResetKey}
        visible={isCatalogueOpen}
        onHide={() => setIsCatalogueOpen(false)}
        onSelect={(template) => {
          setSelectedTemplateIdFromCatalogue(template.id);
          setIsCatalogueOpen(false);
          setIsModalOpen(true);
        }}
        entryPoint="phishing-simulation"
      />
      {isFreeTrial && aiTargetedPhishingModals}
    </>
  );
};
